/**
 * Placement UI permissions — single source of truth for routes, nav, and in-page gates.
 * RBAC Migration (Phase F): Migrated from role-based to capability-based checks.
 * Use effectiveCapabilities where available; falls back to role for backward compatibility.
 */

/**
 * Placement overview requires placement_operations responsibility (institution scope).
 * Checks for VIEW_MOCK_DRIVE (core placement operations capability).
 * @param {object} user
 * @param {string[]} [effectiveCapabilities]
 */
export function canViewPlacementOverview(user, effectiveCapabilities) {
  if (user?.role === 'admin') return true;
  if (effectiveCapabilities?.includes('VIEW_MOCK_DRIVE')) return true;
  // Fallback for backward compatibility during migration
  return user?.role === 'cbcfaculty';
}

/**
 * Mock campaign management requires placement_operations responsibility.
 * Checks for MANAGE_MOCK_DRIVE (placement operations capability).
 * @param {object} user
 * @param {string[]} [effectiveCapabilities]
 */
export function canManageMockCampaigns(user, effectiveCapabilities) {
  if (user?.role === 'admin') return true;
  if (effectiveCapabilities?.includes('MANAGE_MOCK_DRIVE')) return true;
  return user?.role === 'cbcfaculty';
}

/**
 * Mock interviews can be conducted by teaching_faculty or placement_operations.
 * Checks for VIEW_BATCH_DASHBOARD (teaching faculty) or MANAGE_MOCK_SESSION (placement ops).
 * @param {object} user
 * @param {string[]} [effectiveCapabilities]
 */
export function canConductMockInterview(user, effectiveCapabilities) {
  if (user?.role === 'admin') return true;
  if (effectiveCapabilities?.includes('VIEW_BATCH_DASHBOARD')) return true;
  if (effectiveCapabilities?.includes('MANAGE_MOCK_SESSION')) return true;
  return user?.role === 'faculty' || user?.role === 'cbcfaculty';
}

/**
 * Coaching insights requires placement_operations responsibility.
 * Checks for MANAGE_MOCK_DRIVE (placement operations capability).
 * @param {object} user
 * @param {string[]} [effectiveCapabilities]
 */
export function canViewCoachingInsights(user, effectiveCapabilities) {
  if (user?.role === 'admin') return true;
  if (effectiveCapabilities?.includes('MANAGE_MOCK_DRIVE')) return true;
  return user?.role === 'cbcfaculty';
}

/**
 * Students needing coaching support (career intelligence attention list).
 * Available to teaching or placement-operations faculty.
 * Checks for VIEW_BATCH_DASHBOARD (teaching faculty) or MANAGE_MOCK_DRIVE (placement ops).
 * @param {object} user
 * @param {string[]} [effectiveCapabilities]
 */
export function canViewCoachingStudents(user, effectiveCapabilities) {
  if (user?.role === 'admin') return true;
  if (effectiveCapabilities?.includes('VIEW_BATCH_DASHBOARD')) return true;
  if (effectiveCapabilities?.includes('MANAGE_MOCK_DRIVE')) return true;
  return user?.role === 'faculty' || user?.role === 'cbcfaculty';
}

/**
 * Faculty/CBC batch placement analytics dashboard.
 * Requires teaching_faculty or placement_operations.
 * Checks for VIEW_BATCH_DASHBOARD (teaching faculty) or MANAGE_MOCK_DRIVE (placement ops).
 * @param {object} user
 * @param {string[]} [effectiveCapabilities]
 */
export function canViewPlacementBatchAnalytics(user, effectiveCapabilities) {
  if (user?.role === 'admin') return true;
  if (effectiveCapabilities?.includes('VIEW_BATCH_DASHBOARD')) return true;
  if (effectiveCapabilities?.includes('MANAGE_MOCK_DRIVE')) return true;
  return user?.role === 'cbcfaculty' || user?.role === 'faculty';
}

/**
 * Company intelligence curation console (Phase 7).
 * Requires placement_intelligence_curation responsibility (admin + explicit grant).
 * Mirrors backend CURATE_COMPANY_INTEL: admin + explicit capability only.
 * @param {object} user
 * @param {string[]} [effectiveCapabilities]
 */
export function canCurateCompanyIntel(user, effectiveCapabilities) {
  if (user?.role === 'admin') return true;
  if (effectiveCapabilities?.includes('CURATE_COMPANY_INTEL')) return true;
  return user?.role === 'cbcfaculty';
}

export function isStudentPlacementUser(user) {
  return user?.role === 'student';
}

/** @deprecated Use canConductMockInterview */
export function canEvaluateInterviews(user) {
  return canConductMockInterview(user);
}

/** @deprecated Use canViewCoachingInsights */
export function canViewCareerOverview(user) {
  return canViewCoachingInsights(user);
}

/** @deprecated Use canViewCoachingStudents */
export function canViewCareerAttention(user) {
  return canViewCoachingStudents(user);
}
